import React, { useState, useEffect } from 'react';
import { Card, Table, Nav, Modal, Form, Button } from 'react-bootstrap';
import { FaEye, FaEdit, FaTrash, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import axios from 'axios';
import './ContactManagement.scss';

const ContactManagement = () => {
  const [key, setKey] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [contactInfo, setContactInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const contactTypes = [
    'Our Office',
    'Phone',
    'Email',
    'Working Hours',
    'Connect With Us'
  ];

  const fetchMessages = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/contact-messages');
      if (response.data.status === 'success') {
        setMessages(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch messages');
      setLoading(false);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get('https://realestate.bonlineco.com/api/contact-information');
      if (response.data.status === 'success') {
        setContactInfo(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch contact information');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        const response = await axios.delete(`https://realestate.bonlineco.com/api/contact-messages/${id}`);
        if (response.data.status === 'success') {
          // Remove the deleted message from the state
          setMessages(messages.filter(message => message.id !== id));
        }
      } catch (err) {
        setError('Failed to delete message');
      }
    }
  };

  const handleView = async (id) => {
    try {
      const response = await axios.get(`https://realestate.bonlineco.com/api/contact-messages/${id}`);
      if (response.data.status === 'success') {
        setSelectedMessage(response.data.data);
        setShowViewModal(true);
      }
    } catch (err) {
      setError('Failed to fetch message details');
    }
  };

  const handleContactInfoUpdate = async (id, updatedData) => {
    try {
      let response;
      if (id) {
        // Update existing contact info
        response = await axios.put(`https://realestate.bonlineco.com/api/contact-information/${id}`, updatedData);
      } else {
        // Create new contact info
        response = await axios.post('https://realestate.bonlineco.com/api/contact-information', updatedData);
      }
      
      if (response.data.status === 'success') {
        fetchContactInfo();
        setShowEditModal(false);
        setEditingContact(null);
      }
    } catch (err) {
      setError('Failed to save contact information');
    }
  };

  const handleContactInfoDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact information?')) {
      try {
        const response = await axios.delete(`https://realestate.bonlineco.com/api/contact-information/${id}`);
        if (response.data.status === 'success') {
          fetchContactInfo();
        }
      } catch (err) {
        setError('Failed to delete contact information');
      }
    }
  };

  useEffect(() => {
    if (key === 'messages') {
      fetchMessages();
    } else if (key === 'contact') {
      fetchContactInfo();
    }
  }, [key]);

  const renderEditModal = () => (
    <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{editingContact?.id ? 'Edit' : 'Add'} Contact Information</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editingContact && (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={editingContact.type}
                onChange={(e) => {
                  setEditingContact({
                    ...editingContact,
                    type: e.target.value,
                    // Reset social_links if type is changed from "Connect With Us"
                    social_links: e.target.value === 'Connect With Us' 
                      ? (editingContact.social_links || {}) 
                      : null
                  });
                }}
              >
                <option value="">Select Type</option>
                {contactTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Value</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editingContact.value}
                onChange={(e) => setEditingContact({
                  ...editingContact,
                  value: e.target.value
                })}
              />
            </Form.Group>
            {editingContact.type === 'Connect With Us' && (
              <Form.Group className="mb-3">
                <Form.Label>Social Links</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Facebook"
                  className="mb-2"
                  value={editingContact.social_links?.facebook || ''}
                  onChange={(e) => setEditingContact({
                    ...editingContact,
                    social_links: {
                      ...editingContact.social_links,
                      facebook: e.target.value
                    }
                  })}
                />
                <Form.Control
                  type="text"
                  placeholder="Twitter"
                  className="mb-2"
                  value={editingContact.social_links?.twitter || ''}
                  onChange={(e) => setEditingContact({
                    ...editingContact,
                    social_links: {
                      ...editingContact.social_links,
                      twitter: e.target.value
                    }
                  })}
                />
                <Form.Control
                  type="text"
                  placeholder="Instagram"
                  className="mb-2"
                  value={editingContact.social_links?.instagram || ''}
                  onChange={(e) => setEditingContact({
                    ...editingContact,
                    social_links: {
                      ...editingContact.social_links,
                      instagram: e.target.value
                    }
                  })}
                />
                <Form.Control
                  type="text"
                  placeholder="LinkedIn"
                  value={editingContact.social_links?.linkedin || ''}
                  onChange={(e) => setEditingContact({
                    ...editingContact,
                    social_links: {
                      ...editingContact.social_links,
                      linkedin: e.target.value
                    }
                  })}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Visible"
                checked={editingContact.is_visible}
                onChange={(e) => setEditingContact({
                  ...editingContact,
                  is_visible: e.target.checked
                })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Display Order</Form.Label>
              <Form.Control
                type="number"
                value={editingContact.display_order}
                onChange={(e) => setEditingContact({
                  ...editingContact,
                  display_order: parseInt(e.target.value)
                })}
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => handleContactInfoUpdate(editingContact.id, editingContact)}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="contact-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">Contact Management</h2>
      </div>

      <Nav variant="tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Nav.Item>
          <Nav.Link eventKey="messages">Messages</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="contact">Contact Information</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="map">Map</Nav.Link>
        </Nav.Item>
      </Nav>

      {key === 'messages' && (
        <Card className="contact-table-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Messages</Card.Title>
              <div className="summary-badge">
                Total: {messages.length} messages
              </div>
            </div>
            {loading ? (
              <div className="text-center py-4">Loading messages...</div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((message) => (
                    <tr key={message.id}>
                      <td>{message.name}</td>
                      <td>{message.email}</td>
                      <td>{message.phone_number}</td>
                      <td>{message.subject}</td>
                      <td className="message-cell" title={message.message}>
                        {message.message.length > 100 
                          ? `${message.message.substring(0, 100)}...` 
                          : message.message}
                      </td>
                      <td>{new Date(message.created_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      })}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-info btn-sm"
                            onClick={() => handleView(message.id)}
                          >
                            <FaEye />
                          </button>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(message.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {key === 'contact' && (
        <Card className="contact-table-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Contact Information</Card.Title>
              <Button variant="primary" onClick={() => {
                setEditingContact({
                  id: null,
                  type: '',
                  value: '',
                  social_links: null,
                  is_visible: true,
                  display_order: contactInfo.length
                });
                setShowEditModal(true);
              }}>
                Add New
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-4">Loading contact information...</div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <Table responsive>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Information</th>
                    <th>Visible</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contactInfo.map((info) => (
                    <tr key={info.id}>
                      <td>{info.type}</td>
                      <td>
                        {info.type === 'Connect With Us' ? (
                          <div className="social-links">
                            {info.social_links?.facebook && (
                              <a href={info.social_links.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                                <FaFacebookF />
                              </a>
                            )}
                            {info.social_links?.twitter && (
                              <a href={info.social_links.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                                <FaTwitter />
                              </a>
                            )}
                            {info.social_links?.instagram && (
                              <a href={info.social_links.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                                <FaInstagram />
                              </a>
                            )}
                            {info.social_links?.linkedin && (
                              <a href={info.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                                <FaLinkedinIn />
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="info-value">{info.value}</div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${info.is_visible ? 'bg-success' : 'bg-secondary'}`}>
                          {info.is_visible ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{info.display_order}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setEditingContact(info);
                              setShowEditModal(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleContactInfoDelete(info.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {key === 'map' && (
        <Card className="map-card mt-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <Card.Title>Location Map</Card.Title>
              <button className="btn btn-primary">
                <FaEdit className="me-2" />
                Update Location
              </button>
            </div>
            <div className="map-placeholder">
              Map will be integrated here
            </div>
          </Card.Body>
        </Card>
      )}
      {/* View Message Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Message Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <div className="message-details">
              <div className="detail-row">
                <strong>Name:</strong>
                <span>{selectedMessage.name}</span>
              </div>
              <div className="detail-row">
                <strong>Email:</strong>
                <span>{selectedMessage.email}</span>
              </div>
              <div className="detail-row">
                <strong>Phone:</strong>
                <span>{selectedMessage.phone_number}</span>
              </div>
              <div className="detail-row">
                <strong>Subject:</strong>
                <span>{selectedMessage.subject}</span>
              </div>
              <div className="detail-row">
                <strong>Message:</strong>
                <p className="message-content">{selectedMessage.message}</p>
              </div>
              <div className="detail-row">
                <strong>Date:</strong>
                <span>
                  {new Date(selectedMessage.created_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      {renderEditModal()}
    </div>
  );
};

export default ContactManagement;
